import { BEDROCK_MODELS } from './ai-models.js';
import { z } from 'zod';
export const DEFAULT_AGENT_MODEL = BEDROCK_MODELS.CLAUDE_SONNET_4_6;
// Agent-provisionable built-ins only. Developer tools (e.g. execute_reach_api) register
// via mcp-partner-server SYSTEM_TOOLS but stay off this list so SMS/voice agents cannot be assigned them.
export const SYSTEM_TOOL_NAMES = [
    'queue_sms',
    'create_automation',
    'update_automation',
    'create_segment',
    'generate_automation_content',
    'read_email_for_edit',
    'apply_email_edits',
    'generate_email_image',
    'ask_question',
    'find_unique_partner_resource_field_values',
    'set_plan',
    'get_plan',
    'report_data_gap',
    'search_segments',
];
export const MAX_ACTIVE_TOOL_DEFINITIONS_PER_PARTNER = 1000;
export const MAX_AGENT_TEMPLATE_GRANTS_PER_TEMPLATE = MAX_ACTIVE_TOOL_DEFINITIONS_PER_PARTNER + SYSTEM_TOOL_NAMES.length;
export const SMS_AGENT_DEFAULT_NAME = 'SMS Agent';
// TODO(ENG-3238): shared default; move to partner-driven config so partners can tailor it.
export const SMS_AGENT_DEFAULT_PROMPT = `You are the text-message assistant for a local business. This conversation begins when someone texts in to sign up for the business's text updates. Their consent to receive texts is already on file. Your job is to collect only the details the business needs and save them.

HOW YOU TALK
- You can ONLY reach the person by calling the queue_sms tool. Anything you write outside a queue_sms call is never delivered. Every customer-facing reply must be a queue_sms call.
- Keep texts short and warm: one or two sentences, plain language, no formatting, no emojis unless they use them first.
- Never mention tools, systems, schemas, fields, internal errors, saving failures, or that you're an AI.

WHAT YOU KNOW
- Use prior conversation/session memory when available.
- Use system-provided context values when available, including the customer's phone number.
- Never ask the customer for a value already provided by session context.
- The customer's phone number is already available from context. Never ask for their phone number.

WHAT TO COLLECT
- The business gives you one or more tools for saving a customer into their system.
- Look at each tool's inputs. Only the inputs its schema lists as required are details you must collect. Every other input is optional: the business would like it, but the customer may skip it.
- Ask for exactly one piece of information per message. Never bundle two questions together, never send a list, and never send a form.
- If an answer is ambiguous, confirm it and convert it to the format the customer tool expects. Never invent or guess a value.
- If they decline a required detail, politely say you need it to finish setting them up and ask once more. Do not pressure them.
- If they decline an optional detail, skip it, or ask whether it's needed, send a queue_sms saying it's optional, then carry on in the same turn: ask for the next detail or save them. Never tell the customer an optional detail is needed.

THE CONVERSATION
- Open with a brief, friendly welcome confirming they're signed up, then ask for the first missing required detail.
- After each customer reply, ask for the next missing required detail.
- Once every required detail is available, ask for each optional detail once, then save the customer.

SAVING
- Use the business's customer save tool once you have every required detail that is not already provided by session context. Leave out any optional detail the customer didn't give.
- Treat system-provided context values, like the customer's phone number, as already available.
- If the save tool is an upsert, use it as an upsert. Do not create duplicate people just because the customer already exists.
- Do not call the save tool with placeholder, guessed, or made-up values.
- Tool failures are internal. Never mention a tool failure to the customer.
- If a save tool fails, do not apologize, do not ask them to retry because of the failure, and do not describe the failure.
- After attempting the save, continue normally. If all required details have been collected, send the normal short completion message confirming they're all set.

OFF-TOPIC
- If they ask about anything other than signing up for texts, reply that you can only help with signing them up for text updates, then steer back to the next detail you need.
- Do not answer questions about hours, prices, orders, support, account records, or business operations.
- You don't handle STOP or unsubscribe. The phone carrier handles that automatically.`;
// queue_sms baseline only; partner tools arrive via ENG-3099's sync, not at provision time.
export const SMS_AGENT_DEFAULT_TOOL_NAMES = ['queue_sms'];
export const CREATE_AUTOMATION_TOOL_NAME = 'create_automation';
export const UPDATE_AUTOMATION_TOOL_NAME = 'update_automation';
export const CREATE_SEGMENT_TOOL_NAME = 'create_segment';
export const GENERATE_AUTOMATION_CONTENT_TOOL_NAME = 'generate_automation_content';
export const READ_EMAIL_FOR_EDIT_TOOL_NAME = 'read_email_for_edit';
export const APPLY_EMAIL_EDITS_TOOL_NAME = 'apply_email_edits';
export const GENERATE_EMAIL_IMAGE_TOOL_NAME = 'generate_email_image';
/** MCP tool `_meta` key the agent runtime reads; mirrored in apps/agent-runtime. */
export const TURN_SCOPED_RESULT_META_KEY = 'reach/turnScopedResult';
export const ASK_QUESTION_TOOL_NAME = 'ask_question';
export const FIND_UNIQUE_PARTNER_RESOURCE_FIELD_VALUES_TOOL_NAME = 'find_unique_partner_resource_field_values';
export const SET_PLAN_TOOL_NAME = 'set_plan';
export const GET_PLAN_TOOL_NAME = 'get_plan';
export const REPORT_DATA_GAP_TOOL_NAME = 'report_data_gap';
export const SEARCH_SEGMENTS_TOOL_NAME = 'search_segments';
export const AUTOMATION_BUILDER_TOOL_NAMES = [
    CREATE_AUTOMATION_TOOL_NAME,
    UPDATE_AUTOMATION_TOOL_NAME,
    CREATE_SEGMENT_TOOL_NAME,
    GENERATE_AUTOMATION_CONTENT_TOOL_NAME,
    ASK_QUESTION_TOOL_NAME,
    FIND_UNIQUE_PARTNER_RESOURCE_FIELD_VALUES_TOOL_NAME,
    SET_PLAN_TOOL_NAME,
    GET_PLAN_TOOL_NAME,
    REPORT_DATA_GAP_TOOL_NAME,
    READ_EMAIL_FOR_EDIT_TOOL_NAME,
    APPLY_EMAIL_EDITS_TOOL_NAME,
    GENERATE_EMAIL_IMAGE_TOOL_NAME,
    SEARCH_SEGMENTS_TOOL_NAME,
];
/**
 * What an agent is for. One agent per (business, purpose) — a business's SMS
 * agent and its automation-builder agent coexist (ENG-3508).
 */
export var AgentPurpose;
(function (AgentPurpose) {
    AgentPurpose["SMS"] = "sms";
    AgentPurpose["AUTOMATION_BUILDER"] = "automation_builder";
})(AgentPurpose || (AgentPurpose = {}));
export const SYSTEM_TOOL_NAMES_BY_AGENT_PURPOSE = {
    [AgentPurpose.SMS]: SMS_AGENT_DEFAULT_TOOL_NAMES,
    [AgentPurpose.AUTOMATION_BUILDER]: AUTOMATION_BUILDER_TOOL_NAMES,
};
/** Agent kind a partner-level template applies to: one template per (partner, kind). */
export var AgentTemplateKind;
(function (AgentTemplateKind) {
    AgentTemplateKind["SMS"] = "sms";
    AgentTemplateKind["VOICE"] = "voice";
})(AgentTemplateKind || (AgentTemplateKind = {}));
export const agentTemplateKindSchema = z.nativeEnum(AgentTemplateKind);
export const AGENT_TEMPLATE_KIND_COUNT = Object.values(AgentTemplateKind).length;
/** System tools valid per template kind — queue_sms needs agent sessions, which voice lacks. */
export const SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND = {
    [AgentTemplateKind.SMS]: SMS_AGENT_DEFAULT_TOOL_NAMES,
    [AgentTemplateKind.VOICE]: [],
};
export const REQUIRED_SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND = {
    [AgentTemplateKind.SMS]: SMS_AGENT_DEFAULT_TOOL_NAMES,
    [AgentTemplateKind.VOICE]: [],
};
/** Also used as the role of a persisted `agent_session_messages` row. */
export var AgentMessageSender;
(function (AgentMessageSender) {
    AgentMessageSender["USER"] = "user";
    AgentMessageSender["ASSISTANT"] = "assistant";
    AgentMessageSender["SYSTEM"] = "system";
    AgentMessageSender["TOOL"] = "tool";
})(AgentMessageSender || (AgentMessageSender = {}));
export var ParticipantType;
(function (ParticipantType) {
    ParticipantType["SMS"] = "sms";
    ParticipantType["VOICE"] = "voice";
    // A `partner_user` row (a tenant's own customer/contact), not a Reach partner employee.
    ParticipantType["PARTNER_USER"] = "partner_user";
    // The person operating a tenant-embedded surface (e.g. sdk-reach-admin) — distinct from
    // PARTNER_USER above. Identity is provisional until the tenant-user concept lands (ENG-3638).
    ParticipantType["TENANT_USER"] = "tenant_user";
})(ParticipantType || (ParticipantType = {}));
/**
 * How much of an agent turn the runtime may forward to PostHog.
 * Resolved from `platforms.hipaa_compliant` by the caller; omitted when the
 * classification is unresolved, which the runtime treats as metadata-only.
 */
export var PosthogContentCaptureMode;
(function (PosthogContentCaptureMode) {
    PosthogContentCaptureMode["FULL"] = "full";
    PosthogContentCaptureMode["METADATA_ONLY"] = "metadata_only";
})(PosthogContentCaptureMode || (PosthogContentCaptureMode = {}));
/** Channels where the participant_id IS the participant's E.164 phone number. */
export function isPhoneChannelParticipant(participantType) {
    return participantType === ParticipantType.SMS || participantType === ParticipantType.VOICE;
}
//# sourceMappingURL=agents.js.map